import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { DndContext, DragOverlay, PointerSensor, pointerWithin, useSensor, useSensors } from '@dnd-kit/core';
import ModalBase from '@components/Modal/ModalBase';
import { Input } from '@components/Input';
import { Select } from '@components/Select';
import { RadioButtonsGroup } from '@components/RadioButtonsGroup';
import { Typography } from '@components/Typography';
import Button from '@components/Button';
import { useTranslation } from '@hooks';
import { alertActions, brandsActions, campaignTemplatesActions } from '@actions';
import { campaignTemplatesService } from '@services';
import { handleApiError } from '@helpers/api';
import { CHANNEL } from '@constants/channels';
import { EMAIL_BLOCK_TYPE, EMAIL_BUTTON_BINDING } from '@constants/campaignTemplates';
import BlockPalette, { BLOCK_ICONS, PALETTE_DRAG_PREFIX } from './BlockPalette';
import BlockCanvas, { CANVAS_DROP_ZONE_ID, COLUMN_ZONE_PREFIX } from './BlockCanvas';
import BlockSettings from './BlockSettings';
import SettingsPanel from './SettingsPanel';
import {
    createBlock,
    createEmptyDesign,
    normalizeDesign,
    findBlock,
    findBlockLocation,
    updateBlockById,
    removeBlockById,
    cloneBlockWithNewIds,
    BLOCK_LABEL_KEYS,
} from './designUtils';

const Modal = styled(ModalBase)({
    '& .modal-base-paper': {
        width: 'calc(100% - 32px)',
        maxWidth: 'none',
        height: 'calc(var(--100vh) - 32px)',
        maxHeight: 'none',
    },
});

const Layout = styled('div')({
    display: 'flex',
    height: '100%',
    minHeight: 0,
    '@media (max-width: 1024px)': {
        flexDirection: 'column',
        height: 'auto',
        overflowY: 'auto',
    },
});

const Sidebar = styled('div')({
    width: 340,
    minWidth: 340,
    borderRight: '1px solid var(--divider)',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    '@media (max-width: 1024px)': {
        width: '100%',
        minWidth: 0,
        borderRight: 'none',
        borderBottom: '1px solid var(--divider)',
    },
});

const SidebarTabs = styled('div')({
    padding: '12px 16px',
    borderBottom: '1px solid var(--divider)',
});

const SidebarBody = styled('div')({
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    '@media (max-width: 1024px)': {
        overflowY: 'visible',
    },
});

const CenterColumn = styled('div')({
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    '@media (max-width: 1024px)': {
        minHeight: 400,
    },
});

const PreviewArea = styled('div')({
    flex: 1,
    minHeight: 0,
    padding: 16,
    background: 'var(--bg-default, #F5F5F5)',
    display: 'flex',
    flexDirection: 'column',
});

const PreviewFrame = styled('iframe', {
    shouldForwardProp: prop => prop !== 'device',
})(({ device }) => ({
    width: device === 'mobile' ? 375 : 640,
    maxWidth: '100%',
    flex: 1,
    minHeight: 0,
    border: '1px solid var(--divider)',
    borderRadius: 8,
    background: '#fff',
    margin: '0 auto',
    display: 'block',
}));

const PreviewPanelSection = styled('div')({
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
});

const DragChip = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 16px',
    borderRadius: 8,
    border: '1px solid var(--primary-main)',
    background: '#fff',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    fontSize: 13,
    fontFamily: 'Rubik, sans-serif',
    '& > svg': {
        width: 20,
        height: 20,
        color: 'var(--primary-main)',
    },
});

const BUILDER_TAB = { SETTINGS: 'settings', CONTENT: 'content', PREVIEW: 'preview' };

const EmailTemplateBuilder = ({ isOpen, onClose, template, isView, onSaved }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const channels = useSelector(state => state.channels.data);
    const brands = useSelector(state => state.brands.data);

    const [name, setName] = useState('');
    const [subject, setSubject] = useState('');
    const [preheader, setPreheader] = useState('');
    const [brandId, setBrandId] = useState('');
    const [design, setDesign] = useState(createEmptyDesign);
    const [selectedBlockId, setSelectedBlockId] = useState(null);
    const [tab, setTab] = useState(BUILDER_TAB.CONTENT);
    const [device, setDevice] = useState('desktop');
    const [previewHtml, setPreviewHtml] = useState('');
    const [previewLoading, setPreviewLoading] = useState(false);
    const [dragType, setDragType] = useState(null);
    const [dragBlockId, setDragBlockId] = useState(null);
    const [dropTarget, setDropTarget] = useState(null);
    const [saving, setSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [testSend, setTestSend] = useState({ email: '', channelId: '' });
    const [testSending, setTestSending] = useState(false);
    // Test-send persists mid-session; track the saved id (not the prop) to avoid a duplicate create on Save.
    const [persistedTemplateId, setPersistedTemplateId] = useState(null);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

    const pointerRef = useRef(null);

    const collisionDetection = args => {
        pointerRef.current = args.pointerCoordinates;
        const activeId = String(args.active.id);
        const activeBlock = activeId.startsWith(PALETTE_DRAG_PREFIX) ? null : findBlock(design.blocks, activeId);

        const isExcluded = id => {
            if (id === activeId) return true;
            if (!activeBlock || activeBlock.type !== EMAIL_BLOCK_TYPE.COLUMNS) return false;
            if (id.startsWith(COLUMN_ZONE_PREFIX) && id.split(':')[1] === activeId) return true;
            return [...(activeBlock.left || []), ...(activeBlock.right || [])].some(child => child.id === id);
        };

        const depth = collision => {
            const id = String(collision.id);
            if (id.startsWith(COLUMN_ZONE_PREFIX)) return 0;
            return findBlockLocation(design.blocks, id)?.containerId ? 1 : 2;
        };

        return pointerWithin({
            ...args,
            droppableContainers: args.droppableContainers.filter(droppable => !isExcluded(String(droppable.id))),
        }).sort((a, b) => depth(a) - depth(b));
    };

    useEffect(() => {
        if (!isOpen) return;
        setPersistedTemplateId(template?.id || null);
        setName(template?.name || '');
        setSubject(template?.subject || '');
        setPreheader(template?.preheader || '');
        setBrandId(template?.brandId || '');
        setDesign(template?.design ? normalizeDesign(template.design) : createEmptyDesign());
        setSelectedBlockId(null);
        setTab(template ? BUILDER_TAB.CONTENT : BUILDER_TAB.SETTINGS);
        setPreviewHtml('');
        setTestSend({ email: '', channelId: '' });
        setIsDirty(false);
        dispatch(brandsActions.getAll());
    }, [isOpen, template, dispatch]);

    const emailChannels = useMemo(() => (channels || []).filter(channel => channel.type === CHANNEL.EMAIL), [channels]);

    const selectedBlock = selectedBlockId ? findBlock(design.blocks, selectedBlockId) : null;

    const otherLinkType = useMemo(() => {
        const linked = design.blocks
            .flatMap(block => [block, ...(block.left || []), ...(block.right || [])])
            .find(
                block =>
                    block.id !== selectedBlockId &&
                    block.type === EMAIL_BLOCK_TYPE.BUTTON &&
                    (block.binding === EMAIL_BUTTON_BINDING.SURVEY || block.binding === EMAIL_BUTTON_BINDING.COUPON),
            );
        return linked?.binding || null;
    }, [design.blocks, selectedBlockId]);

    const touch = () => setIsDirty(true);

    const updateBlocks = blocks => {
        setDesign(current => ({ ...current, blocks }));
        touch();
    };

    const insertBlock = (type, index) => {
        const block = createBlock(type);
        const blocks = [...design.blocks];
        blocks.splice(index === -1 ? blocks.length : index, 0, block);
        updateBlocks(blocks);
        setSelectedBlockId(block.id);
    };

    const onAddBlock = type => insertBlock(type, design.blocks.length);

    const onChangeBlock = patch => {
        updateBlocks(updateBlockById(design.blocks, selectedBlockId, patch));
    };

    const insertAt = (blocks, location, block) => {
        if (location.containerId === null) {
            const next = [...blocks];
            next.splice(location.index, 0, block);
            return next;
        }
        return blocks.map(container =>
            container.id === location.containerId
                ? {
                      ...container,
                      [location.side]: [
                          ...(container[location.side] || []).slice(0, location.index),
                          block,
                          ...(container[location.side] || []).slice(location.index),
                      ],
                  }
                : container,
        );
    };

    const onDuplicateBlock = id => {
        const location = findBlockLocation(design.blocks, id);
        const source = findBlock(design.blocks, id);
        if (!location || !source) return;
        const copy = cloneBlockWithNewIds(source);
        updateBlocks(insertAt(design.blocks, { ...location, index: location.index + 1 }, copy));
        setSelectedBlockId(copy.id);
    };

    const onRemoveBlock = id => {
        updateBlocks(removeBlockById(design.blocks, id));
        if (selectedBlockId === id) setSelectedBlockId(null);
    };

    const onSelectBrand = value => {
        setBrandId(value);
        const appearance = (brands || []).find(brand => brand.id === value)?.appearance;
        if (appearance) {
            setDesign(current => ({
                ...current,
                styles: {
                    ...current.styles,
                    ...(appearance.backgroundColor ? { bg: appearance.backgroundColor } : {}),
                    ...(appearance.buttonBackgroundColor ? { buttonColor: appearance.buttonBackgroundColor } : {}),
                },
            }));
        }
        touch();
    };

    const onSelectBlock = id => {
        setSelectedBlockId(id);
        setTab(BUILDER_TAB.CONTENT);
    };

    const onDragStart = ({ active }) => {
        const id = String(active.id);
        if (id.startsWith(PALETTE_DRAG_PREFIX)) {
            setDragType(id.slice(PALETTE_DRAG_PREFIX.length));
            return;
        }
        const block = findBlock(design.blocks, id);
        if (block) {
            setDragType(block.type);
            setDragBlockId(id);
        }
    };

    const parseColumnZone = overId => {
        if (!String(overId).startsWith(COLUMN_ZONE_PREFIX)) return null;
        const [, containerId, side] = String(overId).split(':');
        return { containerId, side };
    };

    const resolveDropTarget = (overId, overRect) => {
        if (overId === CANVAS_DROP_ZONE_ID) {
            return { containerId: null, side: null, index: design.blocks.length };
        }
        const zone = parseColumnZone(overId);
        if (zone) {
            const container = design.blocks.find(block => block.id === zone.containerId);
            return { ...zone, index: (container?.[zone.side] || []).length };
        }
        const location = findBlockLocation(design.blocks, overId);
        if (!location) return null;
        const pointerY = pointerRef.current?.y;
        const isBelowMidpoint = overRect && pointerY != null && pointerY > overRect.top + overRect.height / 2;
        return isBelowMidpoint ? { ...location, index: location.index + 1 } : location;
    };

    const draggedType = activeId =>
        activeId.startsWith(PALETTE_DRAG_PREFIX)
            ? activeId.slice(PALETTE_DRAG_PREFIX.length)
            : findBlock(design.blocks, activeId)?.type;

    const sameTarget = (a, b) =>
        a === b || (a && b && a.containerId === b.containerId && a.side === b.side && a.index === b.index);

    const onDragMove = ({ active, over }) => {
        if (isView || !over) return;
        const activeId = String(active.id);
        const target = resolveDropTarget(String(over.id), over.rect);
        if (!target) return;
        if (target.containerId && draggedType(activeId) === EMAIL_BLOCK_TYPE.COLUMNS) {
            setDropTarget(null);
            return;
        }
        if (dragBlockId && target.containerId === null) {
            const from = findBlockLocation(design.blocks, dragBlockId);
            if (from?.containerId && design.blocks[target.index]?.id === from.containerId) {
                setDropTarget(null);
                return;
            }
        }
        setDropTarget(current => (sameTarget(current, target) ? current : target));
    };

    const onDragEnd = ({ active, over }) => {
        setDragType(null);
        setDragBlockId(null);
        const target = dropTarget;
        setDropTarget(null);
        if (isView || !target || !over) return;

        const activeId = String(active.id);
        if (target.containerId && draggedType(activeId) === EMAIL_BLOCK_TYPE.COLUMNS) return;

        if (activeId.startsWith(PALETTE_DRAG_PREFIX)) {
            const block = createBlock(activeId.slice(PALETTE_DRAG_PREFIX.length));
            updateBlocks(insertAt(design.blocks, target, block));
            setSelectedBlockId(block.id);
            return;
        }

        const source = findBlock(design.blocks, activeId);
        const from = findBlockLocation(design.blocks, activeId);
        if (!source || !from) return;
        const sameList = from.containerId === target.containerId && from.side === target.side;
        const index = sameList && from.index < target.index ? target.index - 1 : target.index;
        if (sameList && index === from.index) return;
        updateBlocks(insertAt(removeBlockById(design.blocks, activeId), { ...target, index }, source));
        setSelectedBlockId(activeId);
    };

    const buildWriteModel = () => ({ name: name.trim(), subject, preheader, brandId: brandId || null, design });

    const saveTemplate = async () => {
        const model = buildWriteModel();
        const saved = persistedTemplateId
            ? await campaignTemplatesService.updateEmailTemplate(persistedTemplateId, model)
            : await campaignTemplatesService.createEmailTemplate(model);
        setPersistedTemplateId(saved.id);
        dispatch(campaignTemplatesActions.get());
        setIsDirty(false);
        return saved;
    };

    const onSave = async () => {
        try {
            setSaving(true);
            const saved = await saveTemplate();
            dispatch(alertActions.success(t('alertMessages.editSuccess')));
            onSaved?.(saved);
            onClose();
        } catch (error) {
            handleApiError(error, 'alertMessages.editFail');
        } finally {
            setSaving(false);
        }
    };

    const loadPreview = async () => {
        try {
            setPreviewLoading(true);
            const result = await campaignTemplatesService.renderEmailTemplateDraft({
                design,
                subject,
                preheader,
                brandId: brandId || null,
            });
            setPreviewHtml(result.html);
        } catch (error) {
            handleApiError(error, 'alertMessages.editFail');
        } finally {
            setPreviewLoading(false);
        }
    };

    const onChangeTab = value => {
        setTab(value);
        if (value === BUILDER_TAB.PREVIEW) loadPreview();
    };

    const onTestSend = async () => {
        if (testSending) return;
        try {
            setTestSending(true);
            const saved = await saveTemplate();
            await campaignTemplatesService.testSendEmailTemplate(saved.id, {
                email: testSend.email,
                channelId: testSend.channelId,
            });
            dispatch(alertActions.success(t('TemplateLibrary.testSendSuccess')));
            onSaved?.(saved);
        } catch (error) {
            handleApiError(error, 'TemplateLibrary.testSendFail');
        } finally {
            setTestSending(false);
        }
    };

    const DragChipIcon = dragType ? BLOCK_ICONS[dragType] : null;

    return (
        <Modal
            isOpen={isOpen}
            handleClose={onClose}
            isDirty={isDirty}
            title={
                isView
                    ? t('TemplateLibrary.viewTemplateTitle')
                    : persistedTemplateId
                      ? t('TemplateLibrary.editTemplateTitle')
                      : t('TemplateLibrary.createTemplateTitle')
            }
            onPrimaryAction={isView ? null : onSave}
            primaryActionText={t('TemplateLibrary.saveTemplate')}
            primaryActionDisabled={saving || !name.trim() || (!isDirty && !!persistedTemplateId)}
        >
            <DndContext
                sensors={sensors}
                collisionDetection={collisionDetection}
                onDragStart={onDragStart}
                onDragMove={onDragMove}
                onDragEnd={onDragEnd}
                onDragCancel={() => {
                    setDragType(null);
                    setDragBlockId(null);
                    setDropTarget(null);
                }}
            >
                <Layout>
                    <Sidebar>
                        <SidebarTabs>
                            <RadioButtonsGroup
                                id="pl-builder-tab"
                                value={tab}
                                onChange={onChangeTab}
                                options={[
                                    { value: BUILDER_TAB.SETTINGS, label: t('TemplateLibrary.tabSettings') },
                                    { value: BUILDER_TAB.CONTENT, label: t('TemplateLibrary.tabContent') },
                                    { value: BUILDER_TAB.PREVIEW, label: t('TemplateLibrary.tabPreview') },
                                ]}
                            />
                        </SidebarTabs>
                        <SidebarBody>
                            {tab === BUILDER_TAB.SETTINGS && (
                                <SettingsPanel
                                    name={name}
                                    subject={subject}
                                    preheader={preheader}
                                    brandId={brandId}
                                    brands={brands}
                                    isView={isView}
                                    onChangeName={value => {
                                        setName(value);
                                        touch();
                                    }}
                                    onChangeSubject={value => {
                                        setSubject(value);
                                        touch();
                                    }}
                                    onChangePreheader={value => {
                                        setPreheader(value);
                                        touch();
                                    }}
                                    onSelectBrand={onSelectBrand}
                                />
                            )}
                            {tab === BUILDER_TAB.CONTENT &&
                                (selectedBlock ? (
                                    <BlockSettings
                                        block={selectedBlock}
                                        styles={design.styles}
                                        onChange={onChangeBlock}
                                        onBack={() => setSelectedBlockId(null)}
                                        isView={isView}
                                        otherLinkType={otherLinkType}
                                    />
                                ) : (
                                    <BlockPalette onAddBlock={onAddBlock} isView={isView} />
                                ))}
                            {tab === BUILDER_TAB.PREVIEW && (
                                <PreviewPanelSection>
                                    {!isView && (
                                        <>
                                            <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                                                {t('TemplateLibrary.sendTestHint')}
                                            </Typography>
                                            <Input
                                                label={t('common.email')}
                                                fullWidth
                                                inputProps={{
                                                    value: testSend.email,
                                                    onChange: e =>
                                                        setTestSend(current => ({ ...current, email: e.target.value })),
                                                }}
                                            />
                                            <Select
                                                label={t('TemplateLibrary.emailChannel')}
                                                fullWidth
                                                value={testSend.channelId}
                                                onChange={({ target: { value } }) =>
                                                    setTestSend(current => ({ ...current, channelId: value }))
                                                }
                                                options={emailChannels.map(channel => ({
                                                    value: channel.id,
                                                    label: channel.name,
                                                }))}
                                            />
                                            <Button
                                                variant="outlined"
                                                disabled={testSending || !testSend.email || !testSend.channelId}
                                                onClick={onTestSend}
                                            >
                                                {t('TemplateLibrary.sendTest')}
                                            </Button>
                                        </>
                                    )}
                                </PreviewPanelSection>
                            )}
                        </SidebarBody>
                    </Sidebar>
                    <CenterColumn>
                        {tab === BUILDER_TAB.PREVIEW ? (
                            <PreviewArea>
                                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
                                    <RadioButtonsGroup
                                        id="pl-builder-device"
                                        value={device}
                                        onChange={setDevice}
                                        options={[
                                            { value: 'desktop', label: t('TemplateLibrary.deviceDesktop') },
                                            { value: 'mobile', label: t('TemplateLibrary.deviceMobile') },
                                        ]}
                                    />
                                </div>
                                {previewLoading ? (
                                    <Typography variant="body2" sx={{ textAlign: 'center', marginTop: '24px' }}>
                                        {t('TemplateLibrary.loadingPreview')}
                                    </Typography>
                                ) : (
                                    <PreviewFrame
                                        device={device}
                                        srcDoc={previewHtml}
                                        title="Email preview"
                                        sandbox=""
                                    />
                                )}
                            </PreviewArea>
                        ) : (
                            <BlockCanvas
                                design={design}
                                selectedBlockId={selectedBlockId}
                                onSelectBlock={onSelectBlock}
                                onDuplicate={onDuplicateBlock}
                                onRemove={onRemoveBlock}
                                isView={isView}
                                brandLogoUrl={(brands || []).find(brand => brand.id === brandId)?.appearance?.logo}
                                dropTarget={dropTarget}
                                dragType={dragType}
                                dragBlockId={dragBlockId}
                            />
                        )}
                    </CenterColumn>
                </Layout>
                <DragOverlay dropAnimation={null}>
                    {dragType ? (
                        <DragChip>
                            {DragChipIcon && <DragChipIcon />}
                            {t(BLOCK_LABEL_KEYS[dragType])}
                        </DragChip>
                    ) : null}
                </DragOverlay>
            </DndContext>
        </Modal>
    );
};

export default EmailTemplateBuilder;
